import ApiRoutes from "@services/routing/ApiRoutes";
import type {
  StreamCallbacks,
  GeoCoordinates,
  ChatStreamRequest,
  StreamEvent
} from "./models";

// Custom error class for authentication errors
export class AuthenticationError extends Error {
  constructor(message: string = "Sesión inválida o expirada") {
    super(message);
    this.name = "AuthenticationError";
  }
}

export class StreamCancelledError extends Error {
  constructor(message: string = "Stream cancelled by user") {
    super(message);
    this.name = "StreamCancelledError";
  }
}

class ChatStreamingService {
  private activeAbortController: AbortController | null = null;

  cancelCurrentStream(): void {
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
  }

  isCancellationError(error: unknown): boolean {
    if (error instanceof StreamCancelledError) {
      return true;
    }
    return error instanceof DOMException && error.name === "AbortError";
  }

  async streamChat(
    message: string,
    sessionId: string,
    languageCode: string = "es",
    coordinates?: GeoCoordinates,
    callbacks: StreamCallbacks = {}
  ): Promise<void> {
    const payload: ChatStreamRequest = {
      message,
      session_id: sessionId,
      language_code: languageCode,
      ...(coordinates && { coordinates })
    };

    const abortController = new AbortController();
    this.activeAbortController = abortController;

    try {
      const response = await fetch(ApiRoutes.streamingChat(), {
        method: "POST",
        headers: {
          ...ApiRoutes.apiHeaderKey(),
          "Content-Type": "application/json",
          Accept: "text/event-stream"
        },
        credentials: "include", // Para que el navegador envíe cookies automáticamente (incluyendo nx-session-id)
        body: JSON.stringify(payload),
        signal: abortController.signal
      });

      if (!response.ok) {
        // Check for authentication errors
        if (response.status === 401) {
          throw new AuthenticationError(
            "Sesión inválida o expirada. Por favor, inicia sesión nuevamente."
          );
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      await this.processStream(reader, callbacks);
    } catch (error) {
      if (this.isCancellationError(error)) {
        throw new StreamCancelledError();
      }
      console.error("Error connecting to chat stream:", error);
      if (callbacks.onError) {
        callbacks.onError("error");
      }
      throw error;
    } finally {
      if (this.activeAbortController === abortController) {
        this.activeAbortController = null;
      }
    }
  }

  async streamChatWithAudio(
    audioBlob: Blob,
    sessionId: string,
    languageCode: string = "es",
    coordinates?: GeoCoordinates,
    callbacks: StreamCallbacks = {}
  ): Promise<void> {
    const abortController = new AbortController();
    this.activeAbortController = abortController;

    try {
      // Determinar el nombre del archivo según el tipo del blob
      let filename = "audio.mp3";
      if (audioBlob.type === "audio/mp4") {
        filename = "audio.m4a";
      } else if (audioBlob.type === "audio/webm") {
        filename = "audio.webm";
      } else if (audioBlob.type === "audio/wav") {
        filename = "audio.wav";
      } else if (audioBlob.type === "audio/aac") {
        filename = "audio.aac";
      }

      const form = new FormData();
      form.append("audio_file", audioBlob, filename);
      form.append("session_id", sessionId);
      form.append("language_code", languageCode);
      if (coordinates) {
        form.append("coordinates", JSON.stringify(coordinates));
      }

      if (audioBlob.size === 0) {
        throw new Error("Audio blob está vacío (size: 0)");
      }

      const response = await fetch(ApiRoutes.streamingChatWithAudio(), {
        method: "POST",
        headers: {
          ...ApiRoutes.apiHeaderKey(),
          Accept: "text/event-stream"
        },
        credentials: "include",
        body: form,
        signal: abortController.signal
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch {
          // Ignorar si no se puede leer el error
        }

        if (response.status === 401) {
          throw new AuthenticationError(
            "Sesión inválida o expirada. Por favor, inicia sesión nuevamente."
          );
        }

        if (response.status === 415) {
          throw new Error(
            `Unsupported Media Type (415): El backend no acepta el formato ${audioBlob.type}. Error: ${errorMessage}`
          );
        }
        if (response.status === 413) {
          throw new Error(
            `Payload Too Large (413): El archivo es demasiado grande. Error: ${errorMessage}`
          );
        }
        if (response.status === 400) {
          throw new Error(
            `Bad Request (400): El backend rechazó la petición. Error: ${errorMessage}`
          );
        }

        throw new Error(errorMessage);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      await this.processStream(reader, callbacks);
    } catch (error) {
      if (this.isCancellationError(error)) {
        throw new StreamCancelledError();
      }
      console.error("Error connecting to audio chat stream:", error);
      if (callbacks.onError) {
        callbacks.onError("error");
      }
      throw error;
    } finally {
      if (this.activeAbortController === abortController) {
        this.activeAbortController = null;
      }
    }
  }

  private async processStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    callbacks: StreamCallbacks
  ): Promise<void> {
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = "";

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Stream completed
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");

        buffer = lines.pop() || "";

        for (const line of lines) {
          // Capture event name
          if (line.startsWith("event: ")) {
            currentEvent = line.substring(7).trim();
            continue;
          }

          if (line.startsWith("data: ")) {
            const jsonData = line.substring(6);

            try {
              const eventData: StreamEvent = JSON.parse(jsonData);
              // If the parsed data doesn't have a type, use the event name
              if (!eventData.type && currentEvent) {
                eventData.type = currentEvent as any;
              }

              this.handleEvent(eventData, callbacks);
              currentEvent = ""; // Reset after processing
            } catch (e) {
              console.error("Error parsing JSON:", e, jsonData);
            }
          }
        }
      }
    } catch (error) {
      if (this.isCancellationError(error)) {
        throw new StreamCancelledError();
      }
      console.error("Error reading stream:", error);
      if (callbacks.onError) {
        callbacks.onError("error");
      }
      throw error;
    }
  }

  private handleEvent(
    eventData: StreamEvent,
    callbacks: StreamCallbacks
  ): void {
    const { type } = eventData;

    switch (type) {
      case "status":
        // Status update received
        if (callbacks.onStatus && eventData.message) {
          callbacks.onStatus(eventData.message);
        }
        break;

      case "token":
        if (callbacks.onToken && eventData.text) {
          callbacks.onToken(eventData.text);
        }
        break;

      case "tool_start":
        // Tool started
        if (callbacks.onToolStart && eventData.tool && eventData.message) {
          callbacks.onToolStart(eventData.tool, eventData.message);
        }
        break;

      case "tool_end":
        // Tool completed
        if (callbacks.onToolEnd && eventData.tool) {
          callbacks.onToolEnd(eventData.tool, eventData.preview);
        }
        break;

      case "final":
        // Final result received
        if (callbacks.onFinal && eventData.message) {
          callbacks.onFinal(eventData.message);
        }
        break;

      case "transcription":
        if (callbacks.onTranscription && eventData.text) {
          callbacks.onTranscription(eventData.text);
        }
        break;

      case "metadata":
        // Metadata received (e.g., sources)
        if (callbacks.onMetadata && eventData.sources) {
          callbacks.onMetadata(eventData.sources);
        }
        break;

      case "error":
        console.error("❌ Error:", eventData.message);
        if (callbacks.onError) {
          callbacks.onError("error");
        }
        break;

      default:
      // Unknown event type
    }
  }

  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async getCurrentLocation(): Promise<GeoCoordinates | null> {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser");
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          resolve(coords);
        },
        (error) => {
          console.warn("Error getting location:", error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  // Check the real geolocation permission state from the browser
  async checkGeolocationPermission(): Promise<
    "granted" | "denied" | "prompt" | "unknown"
  > {
    if (!navigator.permissions) {
      console.warn("Permissions API not supported by this browser");
      return "unknown";
    }

    try {
      const result = await navigator.permissions.query({ name: "geolocation" });
      return result.state as "granted" | "denied" | "prompt";
    } catch (error) {
      console.warn("Error checking geolocation permission:", error);
      return "unknown";
    }
  }

  // Request location with detailed permission handling
  async requestLocationWithPermission(): Promise<{
    coordinates: GeoCoordinates | null;
    permissionState: "granted" | "denied" | "prompt" | "unknown";
    error?: string;
  }> {
    if (!navigator.geolocation) {
      return {
        coordinates: null,
        permissionState: "unknown",
        error: "Geolocation not supported by browser"
      };
    }

    try {
      // First check the permission state
      const permissionState = await this.checkGeolocationPermission();

      if (permissionState === "denied") {
        return {
          coordinates: null,
          permissionState: "denied",
          error: "Geolocation permission denied by user"
        };
      }

      // Try to get the location
      const coordinates = await this.getCurrentLocation();

      if (coordinates) {
        return {
          coordinates,
          permissionState: "granted",
          error: undefined
        };
      } else {
        return {
          coordinates: null,
          permissionState: "denied",
          error: "Failed to get location"
        };
      }
    } catch (error) {
      return {
        coordinates: null,
        permissionState: "denied",
        error: error instanceof Error ? error.message : "Unknown error"
      };
    }
  }
}

export const chatStreamingService = new ChatStreamingService();
export default chatStreamingService;
